import { useHomeQuery } from '@/queries/home.query';
import { AnimatePresence, motion } from 'framer-motion';
import { MoveLeft, MoveRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Body, H2, H3 } from './ui/typography/Typography';

/* ========================
 * ANIMATION VARIANTS
 * ======================== */
const imageVariants = {
  initial: {
    opacity: 0,
    scale: 1.05,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 1.02,
    transition: {
      duration: 0.7,
      ease: 'easeInOut',
    },
  },
};

const contentVariants = {
  initial: { opacity: 0, y: 24 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.35 },
  },
};

export default function AboutSection() {
  const { data, isLoading, isError } = useHomeQuery();
  const [index, setIndex] = useState(0);

  const slides = useMemo(() => {
    return (
      data?.data?.kenal_lebih_dekat
        ?.slice()
        .sort((a, b) => a.position - b.position)
        .map((item) => ({
          title: 'Kenal Lebih Dekat dengan AKR Syariah',
          subtitle: item.title,
          desc: item.description,
          image: item.banner,
        })) ?? []
    );
  }, [data]);

  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);

  if (isLoading || isError || !slides.length) return null;

  const current = slides[index];

  return (
    <div className="relative w-full overflow-hidden">
      {/* ================= MOBILE ================= */}
      <div className="block sm:hidden">
        {/* IMAGE */}
        <div className="relative h-[250px] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              src={current.image}
              alt={current.subtitle}
              variants={imageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </AnimatePresence>
        </div>

        {/* CONTENT (container tetap) */}
        <div className="px-5 py-8 bg-white">
          <H2 className="py-2 font-semibold">{current.title}</H2>
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <H3 className="py-2 font-bold text-primary-600">{current.subtitle}</H3>
              <Body className="leading-relaxed mb-4">{current.desc}</Body>
            </motion.div>
          </AnimatePresence>

          {/* NAV */}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={prev} className="p-3 text-primary-600">
              <MoveLeft size={25} />
            </button>
            <button onClick={next} className="p-3 text-primary-600">
              <MoveRight size={25} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= DESKTOP ================= */}
      <div className="hidden sm:flex relative w-full sm:h-[500px] lg:h-[680px] overflow-hidden">
        {/* IMAGE */}
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={current.image}
            alt={current.subtitle}
            variants={imageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* GRADIENT */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/20 to-transparent z-10" />

        {/* CONTENT BOX — STABIL */}
        <div
          className="
            absolute left-0 sm:top-1/2 -translate-y-1/2
            px-16 py-12 w-[640px]
            bg-white/70 backdrop-blur
            rounded-tr-2xl rounded-br-2xl
            shadow-xl z-20
          "
        >
          <H2 className="py-2 font-semibold">{current.title}</H2>
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <H3 className="py-2 font-bold text-primary-600">{current.subtitle}</H3>
              <Body className="leading-relaxed mb-6">{current.desc}</Body>
            </motion.div>
          </AnimatePresence>

          {/* NAV — TIDAK ANIMATE */}
          <div className="flex gap-3 mt-2">
            <button onClick={prev} className="p-3 text-primary-600">
              <MoveLeft size={30} />
            </button>
            <button onClick={next} className="p-3 text-primary-600">
              <MoveRight size={30} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
